/*
 * The Kai voice "Pulse Orb": a revolving sphere of stardust that hears Kai and
 * the caller separately. Framework-free; the dashboard's softphone keeps a copy
 * of the same code (gleap-frontend src/components/Softphone/callWidget/kaiVoiceOrb.ts
 * and kaiVoiceOrbMotion.ts, only formatted differently), so change both together.
 * This file draws; kaiVoiceOrbMotion.ts follows the voices and moves things.
 *
 * - Kai speaking: the surface ripples like a liquid, each syllable onset kicks
 *   the sphere outward on a spring, the brand-tinted stars and a brand glow
 *   light up, and the spin picks up.
 * - Caller speaking: the sphere draws in and slows (listening), each syllable
 *   sends a ring of light out from the centre, the white stars brighten under
 *   a cool halo.
 * - Silence: a slow breath. Thinking: the stars gather in a breath around a
 *   mint core while the sphere winds up its spin.
 */

import {
  createState,
  OrbState,
  pruneRings,
  Ring,
  RING_LIFE_MS,
  stepLevels,
  stepMotion,
  stepThinking,
  VoiceLevels,
} from "./kaiVoiceOrbMotion";

export { SILENT_LEVELS } from "./kaiVoiceOrbMotion";
export type { VoiceLevels } from "./kaiVoiceOrbMotion";

export interface PulseOrbOptions {
  /** Brand colours (hex); the tinted stars and Kai's glow come from them. */
  palette: string[];
  random: () => number;
  /** One still frame, nothing reacts. */
  reducedMotion: boolean;
}

export interface PulseOrbFrame {
  context: CanvasRenderingContext2D;
  /** The canvas in CSS px (ambient stars spread over it). */
  width: number;
  height: number;
  /** The sphere's centre and the side of the box it sits in, in CSS px. */
  centerX: number;
  centerY: number;
  size: number;
  levels: VoiceLevels;
  thinking: boolean;
}

const TAU = Math.PI * 2;
const SPHERE_PARTICLE_COUNT = 520;
const AMBIENT_PARTICLE_COUNT = 46;
const TINTED_SHARE = 0.45;
const WHITE = "rgb(240, 244, 242)";

// Thinking (unchanged feel): a 3.8 s gather-breath around a mint core.
const THINK_CYCLE_MS = 3800;
const THINK_CONTRACTION = 0.22;
const THINK_PHASE_SPREAD = 0.18;
const THINK_CORE_ALPHA = 0.45;
const THINK_CORE_COLOR = "140, 232, 214";
// Fixed axes of the liquid surface, in view space: stars flow through a standing shape.
const AXES = [
  [0.8, 0.5, 0.33],
  [-0.4, 0.85, -0.35],
  [0.2, -0.3, 0.93],
];
const TILT = 0.32;
const SIN_TILT = Math.sin(TILT);
const COS_TILT = Math.cos(TILT);

interface Particle {
  x: number;
  y: number;
  z: number;
  /** Distance from the centre (0..1): inner stars move less. */
  r: number;
  size: number;
  twinklePhase: number;
  twinkleSpeed: number;
  /** Index into the colours; 0 = white. */
  tint: number;
  thinkPhase: number;
}

interface AmbientParticle {
  u: number;
  v: number;
  size: number;
  twinklePhase: number;
  alpha: number;
}

const hexToRgb = (hex: string): [number, number, number] | null => {
  const normalized = hex.replace("#", "");
  const value =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
};

/** `r, g, b` moved `amount` (0..1) toward white. */
const towardWhite = (hex: string, amount: number): string => {
  const rgb = hexToRgb(hex) ?? [168, 148, 255];
  return rgb.map((channel) => Math.round(channel + (255 - channel) * amount)).join(", ");
};

const createParticles = ({ random, tints }: { random: () => number; tints: number }): Particle[] =>
  Array.from({ length: SPHERE_PARTICLE_COUNT }, () => {
    // Uniform direction; radius biased outward for a full core with a soft edge.
    const theta = random() * TAU;
    const cosPhi = random() * 2 - 1;
    const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi));
    const r = Math.pow(random(), 0.42);
    return {
      x: sinPhi * Math.cos(theta) * r,
      y: cosPhi * r,
      z: sinPhi * Math.sin(theta) * r,
      r,
      size: 0.55 + random() * 0.9,
      twinklePhase: random() * TAU,
      twinkleSpeed: 0.5 + random(),
      tint: random() < TINTED_SHARE ? 1 + Math.floor(random() * tints) : 0,
      thinkPhase: random() * THINK_PHASE_SPREAD,
    };
  });

const createAmbient = (random: () => number): AmbientParticle[] =>
  Array.from({ length: AMBIENT_PARTICLE_COUNT }, () => ({
    u: random(),
    v: random(),
    size: 0.4 + random() * 0.7,
    twinklePhase: random() * TAU,
    alpha: 0.08 + random() * 0.2,
  }));

interface Disc {
  x: number;
  y: number;
  radius: number;
  fill: CanvasGradient;
}

const fillDisc = (context: CanvasRenderingContext2D, { x, y, radius, fill }: Disc) => {
  context.globalAlpha = 1;
  context.fillStyle = fill;
  context.beginPath();
  context.arc(x, y, radius, 0, TAU);
  context.fill();
};

/** One radial gradient from the centre: `stops` are [offset, `r, g, b`, alpha]. */
const radialDisc = (
  context: CanvasRenderingContext2D,
  {
    x,
    y,
    inner,
    radius,
    stops,
  }: {
    x: number;
    y: number;
    inner: number;
    radius: number;
    stops: Array<[number, string, number]>;
  },
): Disc => {
  const fill = context.createRadialGradient(x, y, inner, x, y, radius);
  for (const [offset, rgb, alpha] of stops) {
    fill.addColorStop(offset, `rgba(${rgb}, ${alpha.toFixed(3)})`);
  }
  return { x, y, radius, fill };
};

interface Backdrop {
  centerX: number;
  centerY: number;
  baseRadius: number;
  glowColor: string;
  thinkWave: number;
}

/** Kai's glow behind the sphere, the caller's cool halo, and the thinking core. */
const drawBackdrop = (context: CanvasRenderingContext2D, { state, view }: { state: OrbState; view: Backdrop }) => {
  const { centerX: x, centerY: y, baseRadius, glowColor, thinkWave } = view;
  const radius = baseRadius * state.scale;
  const agentFast = state.agentFast.value;
  const glowAlpha = Math.min(0.75, 0.34 * state.agentSlow.value + 0.32 * agentFast);
  if (glowAlpha > 0.005) {
    const stops: Array<[number, string, number]> = [
      [0, glowColor, glowAlpha],
      [0.5, glowColor, glowAlpha * 0.35],
      [1, glowColor, 0],
    ];
    fillDisc(
      context,
      radialDisc(context, {
        x,
        y,
        inner: 0,
        radius: radius * (1.35 + 0.35 * agentFast),
        stops,
      }),
    );
  }
  const haloAlpha = Math.min(0.4, 0.16 * state.callerSlow.value);
  if (haloAlpha > 0.005) {
    const halo = "220, 240, 236";
    const stops: Array<[number, string, number]> = [
      [0, halo, 0],
      [0.55, halo, haloAlpha],
      [1, halo, 0],
    ];
    fillDisc(
      context,
      radialDisc(context, {
        x,
        y,
        inner: radius * 0.6,
        radius: radius * 1.4,
        stops,
      }),
    );
  }
  if (state.thinkAmount > 0) {
    const coreAlpha = THINK_CORE_ALPHA * thinkWave * state.thinkAmount;
    const stops: Array<[number, string, number]> = [
      [0, THINK_CORE_COLOR, coreAlpha],
      [0.55, THINK_CORE_COLOR, coreAlpha * 0.25],
      [1, THINK_CORE_COLOR, 0],
    ];
    fillDisc(
      context,
      radialDisc(context, {
        x,
        y,
        inner: 0,
        radius: baseRadius * (0.55 + 0.25 * thinkWave),
        stops,
      }),
    );
  }
};

/** Ambient far stars: static, a faint slow twinkle, a touch brighter while Kai talks. */
const drawAmbient = (
  context: CanvasRenderingContext2D,
  {
    ambient,
    frame,
    state,
    reducedMotion,
  }: {
    ambient: AmbientParticle[];
    frame: { time: number; width: number; height: number };
    state: OrbState;
    reducedMotion: boolean;
  },
) => {
  context.fillStyle = WHITE;
  for (const star of ambient) {
    const twinkle = reducedMotion ? 0.7 : 0.55 + 0.45 * Math.sin(frame.time * 0.00035 + star.twinklePhase);
    context.globalAlpha = star.alpha * twinkle * (1 + 0.6 * state.agentSlow.value);
    context.beginPath();
    context.arc(star.u * frame.width, star.v * frame.height, star.size, 0, TAU);
    context.fill();
  }
};

/** How far the caller's rings push a star `fromCentre` (0..1 of the disc) out right now. */
const ringBump = ({ rings, time, fromCentre }: { rings: Ring[]; time: number; fromCentre: number }) => {
  let bump = 0;
  for (const ring of rings) {
    const progress = (time - ring.start) / RING_LIFE_MS;
    const distance = fromCentre - progress * 1.3;
    bump += Math.exp(-(distance * distance) / 0.014) * ring.strength * (1 - progress);
  }
  return bump;
};

/** The standing liquid surface at a view-space direction, -1..1. */
const surfaceWave = ({ dx, dy, dz, waveTime }: { dx: number; dy: number; dz: number; waveTime: number }) =>
  0.5 * Math.sin((dx * AXES[0][0] + dy * AXES[0][1] + dz * AXES[0][2]) * 2.4 + waveTime + 0.3) +
  0.3 * Math.sin((dx * AXES[1][0] + dy * AXES[1][1] + dz * AXES[1][2]) * 3.8 - waveTime * 1.6 + 1.7) +
  0.2 * Math.sin((dx * AXES[2][0] + dy * AXES[2][1] + dz * AXES[2][2]) * 6.1 + waveTime * 2.4 + 4.1);

interface SpherePass {
  time: number;
  centerX: number;
  centerY: number;
  radius: number;
  sinR: number;
  cosR: number;
  thinkPhase: number;
  reducedMotion: boolean;
  colors: string[];
}

/** One star: revolve, deform, project, then light it by who is speaking. */
const drawStar = (
  context: CanvasRenderingContext2D,
  { particle, state, pass }: { particle: Particle; state: OrbState; pass: SpherePass },
) => {
  const agentFast = state.agentFast.value;
  const callerFast = state.callerFast.value;
  let gather = 0;
  if (state.thinkAmount > 0) {
    gather = ((1 - Math.cos((pass.thinkPhase - particle.thinkPhase) * TAU)) / 2) * state.thinkAmount;
  }
  // Revolve around Y, then tilt around X.
  const rx = particle.x * pass.cosR + particle.z * pass.sinR;
  const rz = -particle.x * pass.sinR + particle.z * pass.cosR;
  const ry = particle.y * COS_TILT - rz * SIN_TILT;
  const rz2 = particle.y * SIN_TILT + rz * COS_TILT;

  // Direction in view space: the liquid shape and the rings stand still while stars pass.
  const inverse = particle.r > 0.001 ? 1 / particle.r : 0;
  const dx = rx * inverse;
  const dy = ry * inverse;
  const dz = rz2 * inverse;
  const fromCentre = Math.sqrt(dx * dx + dy * dy);
  const bump = ringBump({ rings: state.rings, time: pass.time, fromCentre });
  const wave = surfaceWave({ dx, dy, dz, waveTime: state.waveTime });
  const ripple = Math.sin(fromCentre * 9 - state.rippleTime) * Math.max(0, dz);
  const displacement = 1 + ((0.035 + 0.4 * agentFast) * wave + 0.09 * callerFast * ripple + 0.34 * bump) * particle.r;

  // Perspective: nearer stars draw larger and brighter.
  const depth = (rz2 + 1) / 2;
  const perspective = 0.72 + depth * 0.5;
  const reach = pass.radius * (1 - THINK_CONTRACTION * gather) * perspective * displacement;

  const twinkle = pass.reducedMotion
    ? 0.5 + 0.5 * Math.abs(Math.sin(particle.twinklePhase))
    : 0.5 + 0.5 * Math.sin(state.twinkleTime * 0.00032 * particle.twinkleSpeed + particle.twinklePhase);
  const lift = particle.tint
    ? 1.3 * agentFast + 0.45 * state.agentSlow.value
    : 0.35 * agentFast + 1.0 * callerFast + 1.6 * bump;
  const grow = 1 + (particle.tint ? 0.8 * agentFast : 0.45 * callerFast + 0.8 * bump);

  context.globalAlpha = Math.min(1, (0.1 + 0.42 * depth) * (0.72 + 0.28 * twinkle) * (1 + lift + gather * 0.2));
  context.fillStyle = pass.colors[particle.tint];
  context.beginPath();
  context.arc(pass.centerX + rx * reach, pass.centerY + ry * reach * 0.96, particle.size * perspective * grow, 0, TAU);
  context.fill();
};

/** Builds the orb; call the returned function once per animation frame. */
export const createPulseOrb = ({ palette, random, reducedMotion }: PulseOrbOptions) => {
  const brand = palette.length > 0 ? palette : ["#A894FF"];
  const colors = [WHITE, ...brand.map((hex) => `rgb(${towardWhite(hex, 0.32)})`)];
  const glowColor = towardWhite(brand[0], 0.05);
  const particles = createParticles({ random, tints: brand.length });
  const ambient = createAmbient(random);
  const state = createState();

  return (time: number, { context, width, height, centerX, centerY, size, levels, thinking }: PulseOrbFrame) => {
    const dt = state.lastTime ? Math.min(time - state.lastTime, 50) : 16;
    state.lastTime = time;
    stepLevels(state, { levels, dt, reducedMotion });
    stepThinking(state, { thinking, dt, reducedMotion });
    if (!reducedMotion) {
      stepMotion(state, { time, dt });
    }
    pruneRings(state, time);

    const thinkPhase = (time % THINK_CYCLE_MS) / THINK_CYCLE_MS;
    const baseRadius = size * 0.3;
    context.globalCompositeOperation = "source-over";
    context.clearRect(0, 0, width, height);
    drawBackdrop(context, {
      state,
      view: {
        centerX,
        centerY,
        baseRadius,
        glowColor,
        thinkWave: (1 - Math.cos(thinkPhase * TAU)) / 2,
      },
    });
    drawAmbient(context, {
      ambient,
      frame: { time, width, height },
      state,
      reducedMotion,
    });

    // Overlapping stars add up, so dense regions bloom when they light up.
    context.globalCompositeOperation = "lighter";
    const rotation = state.spin + state.thinkSpin;
    const pass: SpherePass = {
      time,
      centerX,
      centerY,
      radius: baseRadius * state.scale,
      sinR: Math.sin(rotation),
      cosR: Math.cos(rotation),
      thinkPhase,
      reducedMotion,
      colors,
    };
    for (const particle of particles) {
      drawStar(context, { particle, state, pass });
    }
    context.globalCompositeOperation = "source-over";
    context.globalAlpha = 1;
  };
};
